import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Pagination, PAGE_SIZE } from "@/components/Pagination";
import { Plus, Calculator, Rows, SquaresFour, Sparkle, Trash, Briefcase, Stack, ArrowSquareOut } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { FilterBar } from "@/components/FilterBar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { HOLDS, HoldBadge } from "@/components/HoldBadge";

const FILTERS_FALLBACK = ["Draft", "Sent", "Accepted", "Declined"];

export const STAGE_BADGE = {
  zinc: "border-zinc-500/25 bg-zinc-500/10 text-zinc-400",
  blue: "border-blue-500/25 bg-blue-500/10 text-blue-400",
  emerald: "border-emerald-500/25 bg-emerald-500/10 text-emerald-400",
  red: "border-red-500/25 bg-red-500/10 text-red-400",
  amber: "border-amber-500/25 bg-amber-500/10 text-amber-400",
  violet: "border-violet-500/25 bg-violet-500/10 text-violet-300",
  cyan: "border-cyan-500/25 bg-cyan-500/10 text-cyan-300",
  pink: "border-pink-500/25 bg-pink-500/10 text-pink-300",
};

export const STAGE_DOT = {
  zinc: "bg-zinc-600",
  blue: "bg-blue-500",
  emerald: "bg-emerald-500",
  red: "bg-red-500",
  amber: "bg-amber-500",
  violet: "bg-violet-500",
  cyan: "bg-cyan-500",
  pink: "bg-pink-500",
};

export const QUOTE_BADGE = {
  Draft: "border-zinc-500/25 bg-zinc-500/10 text-zinc-400",
  Sent: "border-blue-500/25 bg-blue-500/10 text-blue-400",
  Accepted: "border-emerald-500/25 bg-emerald-500/10 text-emerald-400",
  Declined: "border-red-500/25 bg-red-500/10 text-red-400",
};

export const QUOTE_ACCENT = {
  Draft: "bg-zinc-600",
  Sent: "bg-blue-500",
  Accepted: "bg-emerald-500",
  Declined: "bg-red-500",
};

const VeinBadge = ({ id }) => (
  <span
    data-testid={`quote-vein-badge-${id}`}
    title="Customer arranged their pieces on the slab — review it in the Design Studio"
    className="ml-2 inline-flex items-center gap-1 rounded-full border border-fuchsia-400/30 bg-fuchsia-500/10 px-2 py-0.5 font-sans text-[9px] font-bold uppercase tracking-widest text-fuchsia-300"
  >
    <Sparkle size={10} weight="fill" /> Vein proposal
  </span>
);


const RowLinks = ({ qt }) => {
  const navigate = useNavigate();
  const go = (e, path) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(path);
  };
  const anyLink = qt.job_id || qt.project_id;
  if (!anyLink) return null;
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {qt.job_id &&
        (qt.job_missing ? (
          <span
            data-testid={`quote-job-removed-${qt.id}`}
            title="The job linked to this quote was removed"
            className="inline-flex items-center gap-1 rounded-full border border-zinc-700 bg-zinc-800/40 px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest text-zinc-500"
          >
            <Briefcase size={11} weight="bold" /> Job removed
          </span>
        ) : (
          <button
            type="button"
            data-testid={`quote-open-job-${qt.id}`}
            onClick={(e) => go(e, `/projects/${qt.job_id}?tab=field`)}
            title="Open the job"
            className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-300 transition-colors duration-150 hover:bg-emerald-500/20"
          >
            <Briefcase size={11} weight="bold" /> Job
            <ArrowSquareOut size={10} weight="bold" />
          </button>
        ))}
      {qt.project_id &&
        (qt.project_missing ? (
          <span
            data-testid={`quote-project-removed-${qt.id}`}
            title="The project linked to this quote was removed"
            className="inline-flex items-center gap-1 rounded-full border border-zinc-700 bg-zinc-800/40 px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest text-zinc-500"
          >
            <Stack size={11} weight="bold" /> Project removed
          </span>
        ) : (
          <button
            type="button"
            data-testid={`quote-open-project-${qt.id}`}
            onClick={(e) => go(e, `/projects/${qt.project_id}`)}
            title="Open the project"
            className="inline-flex items-center gap-1 rounded-full border border-indigo-500/40 bg-indigo-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-indigo-300 transition-colors duration-150 hover:bg-indigo-500/20"
          >
            <Stack size={11} weight="bold" /> Project
            <ArrowSquareOut size={10} weight="bold" />
          </button>
        ))}
    </span>
  );
};


export default function Quotes() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stages, setStages] = useState([]);
  const [filter, setFilter] = useState([]);
  const [q, setQ] = useState("");
  const [layout, setLayout] = useState(
    () => localStorage.getItem("bg_quotes_view") || "table",
  );
  const [showEmptyOnly, setShowEmptyOnly] = useState(false);
  const [showProposalsOnly, setShowProposalsOnly] = useState(false);
  const [showLinkedOnly, setShowLinkedOnly] = useState(false);
  const [hold, setHold] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    localStorage.setItem("bg_quotes_view", layout);
  }, [layout]);

  useEffect(() => {
    api
      .get("/settings/quote-stages")
      .then(({ data }) => setStages(data.stages || []))
      .catch(() => setStages([]));
  }, []);

  const stageColor = (name) =>
    stages.find((s) => s.name === name)?.color || "zinc";
  const FILTERS = [
    "All",
    ...(stages.length ? stages.map((s) => s.name) : FILTERS_FALLBACK),
  ];

  const load = useCallback(async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    const { data } = await api.get("/quotes", { params });
    setItems(data);
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const stats = useMemo(() => {
    const open = items.filter(
      (i) => i.status === "Draft" || i.status === "Sent",
    );
    const won = items.filter((i) => i.status === "Accepted");
    const sum = (list) => list.reduce((n, i) => n + (i.totals?.total || 0), 0);
    return {
      open: open.length,
      openValue: sum(open),
      won: won.length,
      wonValue: sum(won),
    };
  }, [items]);

  const money = (v) =>
    `$${(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  // A "blank" draft: no account, no material, no sq ft, no total, no pieces/lines.
  const emptyDrafts = useMemo(
    () =>
      items.filter(
        (i) =>
          i.status === "Draft" &&
          !i.customer_id &&
          !(i.slab_color || "").trim() &&
          !i.totals?.billed_sqft &&
          !i.totals?.total &&
          !(i.lines || []).length &&
          !(i.areas || []).some((a) => (a.pieces || []).length),
      ),
    [items],
  );
  const pendingProposals = useMemo(
    () => items.filter((i) => i.vein_proposal_pending),
    [items],
  );
  const linkedQuotes = useMemo(
    () => items.filter((i) => i.job_id || i.project_id),
    [items],
  );
  const view = (showEmptyOnly
    ? emptyDrafts
    : showProposalsOnly
      ? pendingProposals
      : items
  )
    .filter((i) => filter.length === 0 || filter.includes(i.status))
    .filter((i) => hold.length === 0 || hold.includes(i.hold_status))
    .filter((i) => !showLinkedOnly || i.job_id || i.project_id);

  const totalPages = Math.max(1, Math.ceil(view.length / PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const paged = view.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);

  const clearEmptyDrafts = async () => {
    if (!emptyDrafts.length) return;
    const n = emptyDrafts.length;
    if (
      !window.confirm(
        `Delete ${n} empty draft${n > 1 ? "s" : ""}? This can't be undone.`,
      )
    )
      return;
    await Promise.all(
      emptyDrafts.map((d) => api.delete(`/quotes/${d.id}`).catch(() => {})),
    );
    toast.success(`Cleared ${n} empty draft${n > 1 ? "s" : ""}`);
    setShowEmptyOnly(false);
    load();
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Quotes" />
      <main className="relative z-10 pb-28 pt-6">
        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="quotes-header-card"
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Sales
              </div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Quotes
              </h1>
              <p className="mt-1 text-sm text-zinc-500">
                Price a job by area, edge profile and cut-outs, then send it or
                turn it into a job
              </p>
            </div>
          </div>

          <div
            className="mt-5 flex flex-wrap items-center gap-2 border-t border-zinc-800 pt-4"
            data-testid="quotes-actions"
          >
            <Button
              data-testid="new-quote-button"
              onClick={() => navigate("/quotes/new")}
              className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <Plus size={15} weight="bold" className="mr-2" /> New Quote
            </Button>
            <div className="hidden overflow-hidden rounded-md border border-zinc-800 sm:flex">
              {[
                ["table", Rows],
                ["cards", SquaresFour],
              ].map(([key, Icon]) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`quotes-view-${key}`}
                  onClick={() => setLayout(key)}
                  title={`${key} view`}
                  className={`flex h-12 items-center gap-2 px-4 text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                    layout === key
                      ? "bg-indigo-500 text-white"
                      : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                  }`}
                >
                  <Icon size={16} weight="bold" /> {key}
                </button>
              ))}
            </div>
            {pendingProposals.length > 0 && (
              <button
                type="button"
                data-testid="quotes-proposals-toggle"
                onClick={() => setShowProposalsOnly((v) => !v)}
                title="Customers have arranged their pieces on the slab and are waiting for you to review"
                className={`flex h-12 items-center gap-2 rounded-md border px-4 text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                  showProposalsOnly
                    ? "border-fuchsia-400/50 bg-fuchsia-500/15 text-fuchsia-200"
                    : "border-fuchsia-400/25 bg-zinc-900 text-fuchsia-300 hover:bg-fuchsia-500/10"
                }`}
              >
                <Sparkle size={15} weight="fill" /> Vein proposals ({pendingProposals.length})
              </button>
            )}
            {linkedQuotes.length > 0 && (
              <button
                type="button"
                data-testid="quotes-linked-toggle"
                onClick={() => setShowLinkedOnly((v) => !v)}
                title="Show only quotes that already have a job or project"
                className={`flex h-12 items-center gap-2 rounded-md border px-4 text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                  showLinkedOnly
                    ? "border-indigo-400/50 bg-indigo-500/15 text-indigo-200"
                    : "border-indigo-400/25 bg-zinc-900 text-indigo-300 hover:bg-indigo-500/10"
                }`}
              >
                <Stack size={15} weight="bold" /> Has job / project ({linkedQuotes.length})
              </button>
            )}
            {emptyDrafts.length > 0 && (
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  data-testid="quotes-empty-toggle"
                  onClick={() => setShowEmptyOnly((v) => !v)}
                  className={`flex h-12 items-center gap-2 rounded-md border px-4 text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                    showEmptyOnly
                      ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
                      : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                  }`}
                >
                  Empty drafts ({emptyDrafts.length})
                </button>
                <button
                  type="button"
                  data-testid="quotes-clear-empty"
                  onClick={clearEmptyDrafts}
                  className="flex h-12 items-center gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-4 text-xs font-semibold uppercase tracking-widest text-red-300 transition-colors duration-150 hover:bg-red-500/20"
                >
                  <Trash size={15} weight="bold" /> Clear empty
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            ["Quotes", items.length, "text-white"],
            ["Open", stats.open, "text-blue-400"],
            ["Open Value", money(stats.openValue), "text-indigo-300"],
            ["Won Value", money(stats.wonValue), "text-emerald-400"],
          ].map(([k, v, c]) => (
            <div
              key={k}
              data-testid={`quote-stat-${k.toLowerCase().replace(/ /g, "-")}`}
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            >
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 sm:text-xs">
                {k}
              </div>
              <div className={`font-mono-data mt-1 text-xl font-bold ${c}`}>
                {v}
              </div>
            </div>
          ))}
        </div>

        <FilterBar
          className="mt-6"
          testidPrefix="quotes"
          search={{
            value: q,
            onChange: setQ,
            placeholder: "Search project, account, code…",
            testid: "quote-search-input",
          }}
          sections={[
            {
              key: "status",
              label: "Stage",
              multi: true,
              values: filter,
              allValue: "All",
              onChange: setFilter,
              options: FILTERS.map((f) => [
                f,
                f,
                `quote-filter-${f.toLowerCase()}`,
              ]),
            },
            {
              key: "hold",
              label: "Waiting",
              multi: true,
              values: hold,
              allValue: "All",
              onChange: setHold,
              options: [
                ["All", "Any", "quote-hold-filter-all"],
                ...HOLDS.map((h) => [h, h, `quote-hold-filter-${h.toLowerCase().replace(/\s+/g, "-")}`]),
              ],
            },
          ]}
        />

        {view.length === 0 ? (
          <div
            data-testid="quotes-empty"
            className="mt-6 rounded-lg border border-dashed border-zinc-800 p-10 text-center"
          >
            <Calculator
              size={26}
              weight="bold"
              className="mx-auto text-zinc-700"
            />
            <p className="mt-3 text-sm text-zinc-400">
              {showEmptyOnly
                ? "No empty drafts to clear."
                : "No quotes yet — start one from a template or by hand."}
            </p>
          </div>
        ) : (
          <>
            {layout === "table" && (
              <div
                className="mt-6 hidden overflow-hidden rounded-lg border border-zinc-800 lg:block"
                data-testid="quotes-table"
              >
                <div className="grid grid-cols-[1.7fr_1.5fr_1fr_0.7fr_0.9fr_1fr] gap-4 border-b border-zinc-800 bg-zinc-950 px-4 py-3 text-[11px] uppercase tracking-widest text-zinc-500">
                  <span>Quote</span>
                  <span>Customer</span>
                  <span>Material</span>
                  <span>Sq ft</span>
                  <span>Stage</span>
                  <span className="text-right">Total</span>
                </div>
                {paged.map((qt) => (
                  <Link
                    key={qt.id}
                    to={`/quotes/${qt.id}`}
                    data-testid={`quote-row-${qt.id}`}
                    className="relative grid grid-cols-[1.7fr_1.5fr_1fr_0.7fr_0.9fr_1fr] items-center gap-4 border-b border-zinc-800/70 bg-zinc-900 px-4 py-3 pl-5 transition-colors duration-150 last:border-0 hover:bg-zinc-800/40"
                  >
                    <span
                      className={`absolute left-0 top-0 h-full w-1.5 ${
                        STAGE_DOT[stageColor(qt.status)] || STAGE_DOT.zinc
                      }`}
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-bold">
                        {qt.project_name}
                      </span>
                      <span className="font-mono-data block text-[11px] text-indigo-300">
                        {qt.code}
                        {qt.job_id && (
                          <span className="ml-2 text-emerald-400">
                            job created
                          </span>
                        )}
                        {qt.vein_proposal_pending && (
                          <VeinBadge id={qt.id} />
                        )}
                      </span>
                      <span className="mt-1 block">
                        <RowLinks qt={qt} />
                      </span>
                    </span>
                    <span
                      className="min-w-0 text-xs text-zinc-400"
                      data-testid={`quote-row-customer-${qt.id}`}
                    >
                      <span className="block truncate font-semibold text-zinc-200">
                        {qt.customer_company || qt.customer_name || "—"}
                      </span>
                      <span className="block truncate">
                        {qt.job_address || qt.customer_address || ""}
                      </span>
                    </span>
                    <span className="min-w-0 truncate text-xs text-zinc-400">
                      {qt.slab_color || "—"}
                      <span className="block text-[10px] uppercase tracking-widest text-zinc-600">
                        {qt.stone_group}
                      </span>
                    </span>
                    <span className="font-mono-data text-sm text-zinc-300">
                      {qt.totals?.billed_sqft || 0}
                    </span>
                    <span className="flex w-fit shrink-0 flex-col items-start gap-1">
                    <span
                      data-testid={`quote-row-stage-${qt.id}`}
                      className={`w-fit shrink-0 rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-widest ${
                        STAGE_BADGE[stageColor(qt.status)] || STAGE_BADGE.zinc
                      }`}
                    >
                      {qt.status}
                    </span>
                    <HoldBadge status={qt.hold_status} since={qt.hold_since} note={qt.hold_note} testId={`quote-row-hold-${qt.id}`} />
                    </span>
                    <span className="font-mono-data text-right text-base font-bold">
                      {money(qt.totals?.total)}
                    </span>
                  </Link>
                ))}
              </div>
            )}
            <div
              className={`mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-3 ${
                layout === "table" ? "lg:hidden" : ""
              }`}
              data-testid="quotes-list"
            >
              {paged.map((qt) => (
                <Link
                  key={qt.id}
                  to={`/quotes/${qt.id}`}
                  data-testid={`quote-card-${qt.id}`}
                  className="group relative overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 p-4 pl-5 transition-all duration-150 hover:-translate-y-0.5 hover:border-indigo-400/40"
                >
                  <span
                    className={`absolute left-0 top-0 h-full w-1.5 ${STAGE_DOT[stageColor(qt.status)] || STAGE_DOT.zinc}`}
                  />
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono-data flex flex-wrap items-center gap-2 text-xs text-indigo-300">
                        {qt.code}
                        {qt.vein_proposal_pending && <VeinBadge id={qt.id} />}
                      </div>
                      <h3 className="truncate text-base font-bold">
                        {qt.project_name}
                      </h3>
                    </div>
                    <span className="flex shrink-0 flex-col items-end gap-1">
                    <span
                      data-testid={`quote-stage-${qt.id}`}
                      className={`shrink-0 rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-widest ${
                        STAGE_BADGE[stageColor(qt.status)] || STAGE_BADGE.zinc
                      }`}
                    >
                      {qt.status}
                    </span>
                    <HoldBadge status={qt.hold_status} since={qt.hold_since} note={qt.hold_note} testId={`quote-hold-${qt.id}`} />
                    </span>
                  </div>

                  <div
                    className="mt-3 space-y-0.5 text-xs text-zinc-400"
                    data-testid={`quote-customer-${qt.id}`}
                  >
                    <div className="truncate text-sm font-semibold text-zinc-200">
                      {qt.customer_company || qt.customer_name || "—"}
                    </div>
                    {(qt.contact_name ||
                      (qt.customer_company && qt.customer_name)) && (
                      <div className="truncate text-zinc-500">
                        {qt.contact_name ||
                          (qt.customer_company ? qt.customer_name : "")}
                      </div>
                    )}
                    {(qt.customer_phone || qt.customer_email) && (
                      <div className="font-mono-data truncate text-zinc-500">
                        {qt.customer_phone || qt.customer_email}
                      </div>
                    )}
                    {(qt.job_address || qt.customer_address) && (
                      <div className="truncate text-zinc-500">
                        {qt.job_address || qt.customer_address}
                      </div>
                    )}
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-zinc-800 pt-3 text-[11px] uppercase tracking-widest text-zinc-500">
                    {qt.slab_color && (
                      <span className="truncate text-zinc-400">
                        {qt.slab_color}
                      </span>
                    )}
                    <span>
                      <span className="font-mono-data text-zinc-300">
                        {qt.totals?.billed_sqft || 0}
                      </span>{" "}
                      ft²
                    </span>
                    {qt.job_id && (
                      <span className="text-emerald-400">job created</span>
                    )}
                    <span className="font-mono-data ml-auto text-base font-bold text-white">
                      {money(qt.totals?.total)}
                    </span>
                  </div>
                  {(qt.job_id || qt.project_id) && (
                    <div className="mt-3 flex flex-wrap items-center gap-1.5">
                      <RowLinks qt={qt} />
                    </div>
                  )}
                </Link>
              ))}
            </div>
            <Pagination
              page={curPage}
              total={view.length}
              onPage={setPage}
              testId="quotes-pagination"
            />
          </>
        )}
      </main>
    </div>
  );
}
